import React, { useRef, useState, useEffect } from "react"
import gql from 'graphql-tag'
import { graphql } from '@apollo/client/react/hoc'
import { Popover, PopoverBody, Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap"
import { useParams } from "react-router-dom"
import withApolloClient from '../../withApolloClient'
import RenameColumnPopOver from "./RenameColumnPopOver"
import ConfirmationPopup from "../../Material/ConfirmationPopup"
import "./RenderEditColumnPopover.css"

const ColumnElementTypesQuery = gql`query ColumnElementTypesQuery {
    __type(name:"TaskRabbitColumnElementTypeChoices") {
        enumValues {
            name
            description
        }
    }
}`

const UpdateBoardColumnMutation = gql`
    mutation UpdateBoardColumnMutation($input: UpdateBoardColumnMutationInput!){
        updateBoardColumn(input:$input){
            ok
            errors{
                field
                messages
            }
        }
    }
`

const CreateBoardColumnMutation = gql`mutation CreateBoardColumnMutation($input: CreateBoardColumnMutationInput!){
    createBoardColumn(input: $input){
        ok
        errors{
            field
            messages
        }
    }
}`

const RenderEditColumnPopover = ({ header, apolloClient, columnElementTypes, refetchBoardQuery }) => {
    const [popoverOpen, setPopoverOpen] = useState(false)
    const [showTypeMenu, setShowTypeMenu] = useState(false)
    const [submenuPosition, setSubmenuPosition] = useState("right")
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [columnToArchive, setColumnToArchive] = useState(null)
    const [selectedType, setSelectedType] = useState("")
    const [renameValue, setRenameValue] = useState("")
    const [renameColumnId, setRenameColumnId] = useState(null)
    const [renameOpen, setRenameOpen] = useState(false)

    const menuItemRef = useRef(null)
    const { boardId } = useParams()

    useEffect(() => {
        if (showTypeMenu && menuItemRef.current) {
            const rect = menuItemRef.current.getBoundingClientRect()
            const viewportWidth = window.innerWidth
            const spaceRight = viewportWidth - rect.right
            const submenuWidth = 230
            setSubmenuPosition(spaceRight < submenuWidth ? "left" : "right")
        }
    }, [showTypeMenu])

    const handleBoardColumnUpdate = async ({ action, columnId, newTitle, newType, columnName }) => {
        if (!apolloClient) return setError("GQL Client not found")
        if (!columnId) return setError("Column ID is required")

        setLoading(true)
        setError("")

        try {
            let input = { columnId }

            switch (action) {
                case "rename":
                    if (!newTitle?.trim()) throw new Error("Column name cannot be empty")
                    input.title = newTitle.trim()
                    break

                case "archive":
                    input.isArchived = true
                    break

                case "changeType":
                    setLoading(true)
                    if (!newType) throw new Error("Please select a type")

                    await apolloClient.mutate({
                        mutation: UpdateBoardColumnMutation,
                        variables: { input: { columnId, isArchived: true } },
                    })

                    await apolloClient.mutate({
                        mutation: CreateBoardColumnMutation,
                        variables: {
                            input: {
                                boardId,
                                columnType: newType,
                                elementType: newType,
                                title: columnName || "New Column",
                                isRequired: false,
                            },
                        },
                    })

                    setShowTypeMenu(false)
                    setSelectedType("")
                    if (refetchBoardQuery) await refetchBoardQuery()
                    setLoading(false)
                    return

                default:
                    throw new Error("Invalid action")
            }

            const { data } = await apolloClient.mutate({
                mutation: UpdateBoardColumnMutation,
                variables: { input },
            })

            if (!data?.updateBoardColumn?.ok) {
                throw new Error(
                    data?.updateBoardColumn?.errors?.[0]?.messages?.[0] || "Column update failed"
                )
            }

            setPopoverOpen(false)
            setConfirmOpen(false)
            setColumnToArchive(null)
            setRenameColumnId(null)
            setRenameValue("")
            setSelectedType("")
            if (refetchBoardQuery) await refetchBoardQuery()
        } catch (err) {
            setError(err.message || "Error updating column")
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            <i id={`${header.id}Ellipsis`} title="More Options" className="fa fa-ellipsis-h task-ellipsis-icon" onClick={() => setPopoverOpen(!popoverOpen)} />

            <Popover trigger="legacy" className="task-header-menu-popover" placement="right" isOpen={popoverOpen} target={`${header.id}Ellipsis`} toggle={() => setPopoverOpen(false)}
            >
                <PopoverBody className="p-0">
                    <ul className="task-header-menu">
                        <li className="menu-item" onClick={() => {
                            const columnId = header.id.replace("custom_column_", "")
                            setRenameColumnId(columnId)
                            setRenameValue(header.title)
                            setRenameOpen(true)
                            setPopoverOpen(false)
                        }}>
                            <i className="fa fa-pencil-square-o" />Rename
                        </li>

                        <li className="menu-item submenu-trigger" ref={menuItemRef} onClick={() => setShowTypeMenu(prev => !prev)}                        >
                            <i className="fa fa-exchange" /> Change Column Type
                            <i className="fa fa-angle-right submenu-arrow" />

                            {showTypeMenu && (
                                <div className={`submenu-popover submenu-${submenuPosition}`}>
                                    <div className="change-type-card">
                                        <div className="change-type-list">
                                            {columnElementTypes?.map((t, index) => {
                                                const type = t.name
                                                const icons = ["fa-font", "fa-list-ol", "fa-list", "fa-hashtag", "fa-calendar", "fa-calendar-o", "fa-clock-o", "fa-database", "fa-tag",]
                                                const icon = icons[index % icons.length]
                                                return (
                                                    <button
                                                        key={type}
                                                        type="button"
                                                        className={`change-type-item-btn`}
                                                        disabled={loading}
                                                        onClick={() => {
                                                            setSelectedType(type)
                                                            handleBoardColumnUpdate({
                                                                action: "changeType",
                                                                columnId: header?.columnNode?.id,
                                                                columnName: header.title,
                                                                newType: type,
                                                            })
                                                            setShowTypeMenu(false)
                                                        }}
                                                    >
                                                        <div className="type-icon-box">
                                                            <i className={`fa ${icon}`} />
                                                        </div>
                                                        <span className="type-label">{type}</span>
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </li>

                        <li className="menu-item" onClick={() => {
                            setColumnToArchive(header?.columnNode?.id)
                            setConfirmOpen(true)
                            setPopoverOpen(false)
                        }}>
                            <i className="fa fa-archive" /> Archive
                        </li>
                    </ul>
                </PopoverBody>
            </Popover>

            {renameOpen && (
                <RenameColumnPopOver isOpen={renameOpen} toggle={() => setRenameOpen(false)} target={`${header.id}Ellipsis`} column={header} refetchBoardQuery={refetchBoardQuery} />
            )}


            <ConfirmationPopup
                open={confirmOpen}
                message="Are you sure you want to archive this column?"
                loading={loading}
                handleClose={() => {
                    setConfirmOpen(false)
                    setError("")
                }}
                action="Yes"
                confirmAction={() => handleBoardColumnUpdate({ action: "archive", columnId: columnToArchive })}
                color="danger"
                error={error}
            />
        </>
    )
}

export default graphql(ColumnElementTypesQuery, {
    props: ({ data: { __type, loading } }) => ({
        columnElementTypes: __type?.enumValues || [],
        columnElementTypesLoading: loading,
    }),
})(withApolloClient(RenderEditColumnPopover))
